import {
  DropdownButton,
  DropdownButtonContext,
  FlexBox,
} from '@dixa/lib-shared-habla';
import { useState } from 'react';
import { Editor, Selection, Transforms } from 'slate';
import { ReactEditor, useSlate } from 'slate-react';

import {
  getLink,
  insertLink,
  isBlockActive,
  isLinkActive,
  isMarkActive,
  removeLink,
  toggleBlock,
  toggleMark,
} from '../richText';

import { LinkMenu } from './linkMenu';
import styles from './styles.module.scss';
import { ToolbarButton } from './toolbarButton';

type ToolbarProps = {
  /** Function that gets called when an image insert is needed. */
  openFileBrowser: () => void;
  /** Flag to indicate if toolbar buttons should be disabled */
  disabled: boolean;
};

/** Renders the editing toolbar (bold, itatic etc) for bulletin editor */
export function Toolbar({
  disabled,
  openFileBrowser,
}: ToolbarProps): JSX.Element {
  const editor = useSlate();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [textValue, setTextValue] = useState('');
  const [urlValue, setUrlValue] = useState('');

  const handleToggleLinkMenu = (isOpen: boolean) => {
    if (!isOpen) {
      setTextValue('');
      setUrlValue('');
      return;
    }
    const selection = editor.selection;
    const selectedText = selection ? Editor.string(editor, selection) : null;
    const link = getLink(editor);
    setTextValue(selectedText || link?.children[0].text || '');
    setUrlValue(link?.href || '');
    setSelection(selection);
  };

  const handleLinkAdd = (url: string, text: string) => {
    // reselect in editor because dialog takes away focus
    ReactEditor.focus(editor);
    if (selection) {
      Transforms.select(editor, selection);
    }
    insertLink(editor, url, text);
  };

  return (
    <FlexBox
      flexWrap="wrap"
      justifyContent="center"
      gap="200"
      className={styles.toolbarContainer}
    >
      <ToolbarButton
        disabled={disabled}
        onClick={() => toggleMark(editor, 'bold')}
        active={isMarkActive(editor, 'bold')}
        icon="bold"
      ></ToolbarButton>

      <ToolbarButton
        disabled={disabled}
        onClick={() => toggleMark(editor, 'italic')}
        active={isMarkActive(editor, 'italic')}
        icon="italic"
      ></ToolbarButton>

      <ToolbarButton
        disabled={disabled}
        onClick={() => toggleMark(editor, 'underline')}
        active={isMarkActive(editor, 'underline')}
        icon="underline"
      ></ToolbarButton>

      <DropdownButton
        className={styles.linkButton}
        noStyle
        onToggle={handleToggleLinkMenu}
        dropdown={
          <DropdownButtonContext.Consumer>
            {({ toggleDropdown, dropdownOpen }) =>
              dropdownOpen ? (
                <LinkMenu
                  initialText={textValue}
                  initialUrl={urlValue}
                  onAddLink={handleLinkAdd}
                  onHide={toggleDropdown}
                  onUnlink={() => removeLink(editor)}
                />
              ) : null
            }
          </DropdownButtonContext.Consumer>
        }
      >
        <ToolbarButton
          disabled={disabled}
          active={isLinkActive(editor)}
          icon="link"
        ></ToolbarButton>
      </DropdownButton>

      <ToolbarButton
        disabled={disabled}
        onClick={() => toggleBlock(editor, 'heading-two')}
        active={isBlockActive(editor, 'heading-two')}
        icon="subtitle"
      >
        Subtitle
      </ToolbarButton>

      <ToolbarButton
        disabled={disabled}
        onClick={() => toggleBlock(editor, 'paragraph')}
        active={isBlockActive(editor, 'paragraph')}
        icon="text"
      >
        Text
      </ToolbarButton>

      <ToolbarButton
        disabled={disabled}
        onClick={openFileBrowser}
        active={false}
        icon="image"
      >
        Insert image
      </ToolbarButton>
    </FlexBox>
  );
}
